import { tracked } from '@glimmer/tracking';
import type { IntlService } from 'ember-intl';
import type ErrorTrackingService from 'frontend-utils/services/error-tracking';
import {
  CHANNEL_TRANSLATIONS,
  isChannelHierarchyItem,
  isKpiCategory,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import { BENI_PMS_MAPPING_FIXED_CATEGORY_TRANSLATIONS } from 'lumen/utils/constants/pms-mapping-constants';

export const ROOT_CATEGORY_ID = 'root'; // A frontend-only dummy id so we can identity the root level. Should not exist in backend.

export interface HierarchyItem {
  fixed: boolean;
  id: string;
  name: string;
  order: number;
  parent_id: string;
}

export type Breakdown = 'market_code' | 'rate_code' | 'channel';

export default class MappingCategoryBenchmark {
  @tracked children: MappingCategoryBenchmark[] = [];
  @tracked isExpanded = true;
  @tracked name;

  breakdown;
  id;
  isFixed;
  isMappingByRateCode;
  order;
  parent: MappingCategoryBenchmark | null;
  parentId;

  get canAddChildCategories(): boolean {
    return this.isExpandable;
  }

  get childrenSorted(): MappingCategoryBenchmark[] {
    const sortedChildren = this.children
      .filter((c) => c.order !== null)
      .sort((c1, c2) => c1.order - c2.order);
    const newChildren = this.children.filter((c) => c.order === null);
    return [...sortedChildren, ...newChildren];
  }

  get depth(): number {
    if (this.isRootCategory) {
      return -1;
    }
    if (this.parent) {
      return this.parent.depth + 1;
    }
    return 0;
  }

  get isExpandable(): boolean {
    return Boolean(this.children.length);
  }

  get isRootCategory(): boolean {
    return this.id === ROOT_CATEGORY_ID;
  }

  get isTopLevelCategory(): boolean {
    const hasNoParent = this.parentId === null && this.parent === null;
    const parentIsRootCategory =
      this.parent !== null && this.parent.isRootCategory;

    return hasNoParent || parentIsRootCategory;
  }

  get maxDepth(): number {
    return 1;
  }

  constructor(
    breakdown: Breakdown,
    id: string,
    name: string | null,
    parentId: string | null,
    isFixed: boolean,
    order: number,
    isMappingByRateCode: boolean,
  ) {
    this.breakdown = breakdown;
    this.id = id;
    this.name = name;
    this.parentId = parentId;
    this.isFixed = isFixed;
    this.order = order;
    this.isMappingByRateCode = isMappingByRateCode;

    this.parent = null;
  }

  addChildCategory(category: MappingCategoryBenchmark): void {
    category.parent = this;
    this.children = [...this.children, category];
  }

  static createRootCategory(breakdown: Breakdown): MappingCategoryBenchmark {
    return new MappingCategoryBenchmark(
      breakdown,
      ROOT_CATEGORY_ID,
      null,
      null,
      false,
      0,
      false,
    );
  }

  static fromApiResponse(
    hierarchyItem: HierarchyItem,
    breakdown: Breakdown,
    isMappingByRateCode: boolean,
  ): MappingCategoryBenchmark {
    return new MappingCategoryBenchmark(
      breakdown,
      hierarchyItem.id,
      hierarchyItem.name,
      hierarchyItem.parent_id,
      hierarchyItem.fixed,
      hierarchyItem.order,
      isMappingByRateCode,
    );
  }

  getDisplayName(
    intl: IntlService,
    errorTracking: ErrorTrackingService,
  ): string | null {
    try {
      let translationKey = null;

      if (isChannelHierarchyItem(this.id)) {
        translationKey = CHANNEL_TRANSLATIONS[this.id];
      } else if (this.name && isKpiCategory(this.name)) {
        translationKey =
          BENI_PMS_MAPPING_FIXED_CATEGORY_TRANSLATIONS[this.name];
      }

      if (translationKey) {
        return intl.t(translationKey);
      }
    } catch (error) {
      errorTracking.captureException(error, {});
    }

    return null;
  }

  // Returns the category display name in a full path format.
  // Example: "Transient > Discount > General Discount > Non Direct".
  getFullPathDisplayName(
    intl: IntlService,
    errorTracking: ErrorTrackingService,
  ): string {
    const parents = this.getParents().reverse();

    parents.push(this);

    const fullPathDisplayName = parents
      .map((category) => {
        return category.getDisplayName(intl, errorTracking);
      })
      .join(' > ');

    return fullPathDisplayName;
  }

  // Returns an array of parents (first parent is the closest parent to the category, last is the furthest parent to the category)
  getParents(): MappingCategoryBenchmark[] {
    const parents = [];

    if (this.parent && this.parent.id !== ROOT_CATEGORY_ID) {
      parents.push(this.parent);
      parents.push(...this.parent.getParents());
    }

    return parents;
  }
}
